import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import ActiveIcon from "@mui/icons-material/Check";
import { notBlank } from "../util/ext";
import { Link, useResolvedPath } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useProjectDelete } from "./cspServices";
import { AlertContext } from "../components/alert/AlertProvider";
import { Box, IconButton } from "@mui/material";
import { equals, not } from "ramda";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },

  contentCell: {
    width: "33%",
  },

  cell: {
    minWidth: 135,
  },
}));

const ProgramTable = ({ program, projects, onScrollVisible }) => {
  const url = useResolvedPath(".").pathname;
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  const [processing, setProcessing] = useState(false);
  const projectDelete = useProjectDelete();
  const handleDeleteClick = useCallback(
    async projectId => {
      setProcessing(true);
      try {
        await projectDelete.mutateAsync({
          resourceIds: { id: projectId },
        });
      } catch (err) {
        alertError(err);
      } finally {
        setProcessing(false);
      }
    },
    [projectDelete, alertError],
  );

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer
        ref={tableContainerEl}
        style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
      >
        <Table stickyHeader aria-label="Projects">
          <TableHead>
            <TableRow>
              <TableCell
                key="project"
                align="left"
                className={classes.contentCell}
              >
                Name
              </TableCell>

              <TableCell
                key="project_key"
                align="left"
                className={classes.cell}
              >
                Project Key
              </TableCell>

              <TableCell
                key="project_active"
                align="center"
                className={classes.cell}
              >
                Active
              </TableCell>

              <TableCell
                key="actions-edit"
                align="center"
                style={{ width: 50 }}
              >
                Edit
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {projects.map(project => (
              <TableRow hover tabIndex={-1} key={project.projectId}>
                <TableCell
                  key={"name:" + project.projectId}
                  align="left"
                  className={classes.contentCell}
                >
                  {project.projectName}
                </TableCell>

                <TableCell
                  key={"key:" + project.projectId}
                  align="left"
                  className={classes.contentCell}
                >
                  {project.projectKey}
                </TableCell>

                <TableCell
                  key={"active:" + project.projectId}
                  align="center"
                  className={classes.contentCell}
                >
                  {project.active && (
                    <ActiveIcon fontSize="large" color="primary" />
                  )}
                </TableCell>

                <TableCell
                  key={"actions-edit:" + project.projectId}
                  align="center"
                  style={{ width: 75 }}
                >
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Edit">
                      <Link
                        component={IconButton}
                        to={{
                          pathname: `${url}/projects/${project.projectId}`,
                        }}
                        state={{
                          label: `Back to ${program.name}`,
                          previousLocation: `/manage-csp/programs/${program.id}`,
                        }}
                      >
                        <EditIcon fontSize="large" color="primary" />
                      </Link>
                    </Tooltip>
                    {not(equals(program.key, "project-trash-can")) && (
                      <ConfirmButton
                        disabled={processing}
                        handleDelete={() =>
                          handleDeleteClick(project.projectId)
                        }
                        dialogTitle="Delete Project"
                        dialogBody="Are you sure you want to delete this project?"
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProgramTable;
